<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="primary" class="mr-1" size="xs" @click="toggle"><i
          class="fas fa-edit"></i>&nbsp;Edit</b-button>
    </slot>

    <b-modal @show="onShow" @hidden="resetModal" ref="modal-container" v-model="modalShow" size="xl" :title="title"
      :static="true" :lazy="true" hide-footer>
      <b-button v-if="!create && isAdmin && formValues.id" variant="outline-primary" class="mb-3"
        @click="createFromTemplate">
        <i class="fas fa-copy" aria-hidden="true"></i> Create operator from this template
      </b-button>
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <div class="modal-content-container" style="display: flex;">
          <div class="tabs-container" style="flex: 1; padding-right: 10px;">
            <b-tabs content-class="mt-3">
              <b-tab title="Details" active>
                <b-form-group v-if="!this.create" label="_ID" label-for="id-input">
                  <b-form-input id="id-input" v-model="formValues.id" autocomplete="off" disabled />
                </b-form-group>

                <b-form-group label="Name" label-for="name-input">
                  <b-form-input id="name-input" v-model="formValues.name" v-validate="{ required: true, min: 3 }"
                    :state="validateState('name')" aria-describedby="name-live-feedback" data-vv-name="name"
                    data-vv-as="Name" autocomplete="off" :disabled="!this.create" />
                  <b-form-invalid-feedback id="name-live-feedback">{{ veeErrors.first('name')
                    }}</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Secret" label-for="secret-input">
                  <b-form-input id="secret-input" v-model="formValues.secret" v-validate="{ required: true, min: 3 }"
                    :state="validateState('secret')" aria-describedby="secret-live-feedback" data-vv-name="secret"
                    data-vv-as="Secret" autocomplete="off" :disabled="!this.create" />
                  <b-form-invalid-feedback id="secret-live-feedback">{{ veeErrors.first('secret')
                    }}</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group v-for="({ id, label }) in urls" v-bind:key="id" :label="label" :label-for="id + '-input'">
                  <b-form-input :id="id + '-input'" v-model="formValues.urls[id]" autocomplete="off" disabled
                    v-if="formValues.urls[id]" />
                </b-form-group>
              </b-tab>

              <b-tab title="Settings">
                <b-form-group label="Bingo ticket lifetime (days)" label-for="engine-bingo-lifetime">
                  <b-form-input id="engine-bingo-lifetime" type="number" min="1" step="1" placeholder="30"
                    v-model.number="formValues.settings.bingoTicketLifetimeDays"
                    v-validate="{ integer: true, min_value: 1 }" data-vv-name="bingoTicketLifetimeDays"
                    data-vv-as="Bingo ticket lifetime" :state="validateState('bingoTicketLifetimeDays')" />
                  <b-form-invalid-feedback>{{ veeErrors.first('bingoTicketLifetimeDays') }}</b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Rounds before redirect" label-for="homeUrl-input">
                  <b-form-input id="homeUrl-input" type="number" v-model="formValues.settings.redirectAfterRoundsWait"
                    autocomplete="off" placeholder="Enter number of rounds" />
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.sendBetDetails" :value="true"
                    :unchecked-value="false">Send
                    bet details</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.enableMinAdjust" :value="true"
                    :unchecked-value="false">Enable
                    min adjust</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.enableMaxAdjust" :value="true"
                    :unchecked-value="false">Enable
                    max adjust</b-form-checkbox>
                </b-form-group>
                <b-form-group label="Hide controls" label-for="sources-input">
                  <multiselect id="filter-type" v-model="chosenHideControls" :options="hideControlsOptions"
                    :custom-label="(opt) => opt.value" track-by="value" :multiple="true" :placeholder="$t('Select')"
                    :select-label="$t('Press enter to select')" :selected-label="$t('Selected')"
                    :hide-selected="true" />
                </b-form-group>
                <HideControlsMapEditor v-model="formValues.settings.hideControlsMap" :controls="hideControlsOptions.map(option => option.value)" />
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.disableNumberCompact" :value="true"
                    :unchecked-value="false">Disable
                    number compact</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.showTimeZone" :value="true"
                    :unchecked-value="false">Show
                    timezone (UTC)</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.disableOppositeBets" :value="true"
                    :unchecked-value="false">Disable
                    opposite bets</b-form-checkbox>
                </b-form-group>

                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.showWorking" :value="true" :unchecked-value="false">Show
                    working</b-form-checkbox>
                </b-form-group>

                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.rollReverse" :value="true" :unchecked-value="false">Roll
                    reverse lobby</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.adjustComboBets" :value="true"
                    :unchecked-value="false">Adjust
                    combo bets to a maximum</b-form-checkbox>
                </b-form-group>
                <b-form-group>
                  <b-form-checkbox v-model="formValues.settings.groupCredit" :value="true"
                    :unchecked-value="false">Group
                    credit</b-form-checkbox>
                </b-form-group>

                <b-form-group label="Rating/store tables"
                  description="Leave empty to enable rating and store on every table.">
                  <multiselect v-model="ratingTables" :options="ratingTableOptions"
                    :custom-label="tableOptionLabel" track-by="value" :multiple="true"
                    :placeholder="$t('Select')" :select-label="$t('Press enter to select')"
                    :selected-label="$t('Selected')" :hide-selected="true" />
                </b-form-group>

                <b-form-group label="Logo" label-for="logo-input">
                  <div style="max-width: 200px" class="mb-2" v-if="formValues.settings && formValues.settings.logo">
                    <b-img :src="this.cdnBackUrl + '/' + formValues.settings.logo + '?t=' + Date.now()" fluid
                      alt="Logo"></b-img>
                  </div>

                  <b-form-file v-model="formValues.logo"
                    v-validate="{ image: true, size: 150, maxDimensions: [660, 300], maxWidthHeightRatio: 2 }"
                    id="logo-input" name="logo-input" :state="validateState('logo-input')"
                    aria-describedby="logo-input-live-feedback" data-vv-as="Logo" no-drop
                    placeholder="Choose a file here..." />
                  <b-form-invalid-feedback id="logo-input-live-feedback">
                    {{ veeErrors.first('logo-input') }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-tab>

              <b-tab title="Lobby settings">
                <b-form-group>
                  <b-form-checkbox v-model="useDefaultSettingsForLobby" :value="true" :unchecked-value="false">Use
                    default
                    settings</b-form-checkbox>
                </b-form-group>

                <b-form-group label="Tables:">
                  <div class="border rounded" :class="{ 'draggable-disabled': useDefaultSettingsForLobby }">
                    <draggable v-model="lobbyTables" group="tables" @start="drag = true" @end="drag = false"
                      :disabled="useDefaultSettingsForLobby" class="row m-1">
                      <div v-for="table in actualLobbyTables" :key="table.id" class="btn-group p-1 col-sm-3"
                        role="group">
                        <div class="btn btn-secondary btn-sm text-ellipsis">
                          <small>{{ table.title }}</small>
                        </div>
                        <button v-if="useDefaultSettingsForLobby === false" type="button"
                          class="btn btn-secondary btn-sm" v-on:click="removeLobbyTable(table.id)"
                          style="flex-grow: initial;">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div class="col-sm-3 p-1">
                        <b-form-select :disabled="useDefaultSettingsForLobby || tableListForLobbyTables.length <= 1"
                          v-model="lobbyTablesAddValue" :options="tableListForLobbyTables" size="sm"
                          v-on:change="addLobbyTable" />
                      </div>

                    </draggable>

                  </div>
                </b-form-group>

                <b-form-group label="Table Types:">
                  <div class="border rounded" :class="{ 'draggable-disabled': useDefaultSettingsForLobby }">
                    <draggable v-model="lobbyTableGroups" group="tableTypes" @start="drag = true" @end="drag = false"
                      :disabled="useDefaultSettingsForLobby" class="row m-1">
                      <div v-for="group in actualLobbyTableGroups" :key="group" class="btn-group p-1 col-sm-3"
                        role="group">
                        <div class="btn btn-secondary btn-sm text-ellipsis">
                          <small>{{ group }}</small>
                        </div>
                        <button v-if="useDefaultSettingsForLobby === false" type="button"
                          class="btn btn-secondary btn-sm" v-on:click="removeLobbyTableGroup(group)"
                          style="flex-grow: initial;">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div class="col-sm-3 p-1">
                        <b-form-select :disabled="useDefaultSettingsForLobby || tableGroupList.length <= 1"
                          v-model="lobbyTableGroupsAddValue" :options="tableGroupList" size="sm"
                          v-on:change="addLobbyTableGroup" />
                      </div>

                    </draggable>
                  </div>
                </b-form-group>
                <b-form-group>
                  <button type="button" :disabled="useDefaultSettingsForLobby" class="btn btn-default"
                    v-on:click="copyLobbySettings"><i class="fa fa-copy"></i> Copy</button>
                  <button type="button" :disabled="useDefaultSettingsForLobby" class="btn btn-default ml-2"
                    v-on:click="pasteLobbySettings"><i class="fa fa-paste"></i> Paste</button>
                </b-form-group>
              </b-tab>

              <b-tab title="Max Coverage">
                <b-form-group label="Default value">
                  <b-form-input id="maxCoverage-input" type="number" v-model="formValues.settings.maxCoverage"
                    autocomplete="off" placeholder="Max Coverage" />
                </b-form-group>

                <div class="row" v-if="maxCoverageItems.length > 0">
                  <div class="col-sm-4">
                    <label>Table ID</label>
                  </div>
                  <div class="col-sm-4">
                    <label>Max Coverage</label>
                  </div>
                </div>

                <div v-for="(item, key) in maxCoverageItems" v-bind:key="key" class="row mb-2">
                  <div class="col-sm-4">
                    <b-form-input type="number" v-model="item.tableId" placeholder="Table ID" />
                  </div>
                  <div class="col-sm-4">
                    <b-form-input type="number" v-model="item.value" placeholder="Max Coverage" />
                  </div>
                  <div class="col-sm-4">
                    <button type="button" class="btn btn-danger" v-on:click.prevent="removeMaxCoverage(key)"><i
                        class="fas fa-trash"></i> Remove</button>
                  </div>
                </div>

                <div>
                  <button type="button" class="btn btn-info" v-on:click.prevent="addMaxCoverage">Add value for
                    table</button>
                </div>

              </b-tab>
            </b-tabs>
          </div>
          <div class="json-editor-container" style="flex: 1; padding-left: 10px;">
            <b-form-group label="Settings JSON" label-for="settings-input">
              <b-alert v-if="settingsChangedWarning" variant="warning" show dismissible class="mb-2">
                <strong>Предупреждение:</strong> Настройки оператора изменены с дефолтных значений.
              </b-alert>
              <v-jsoneditor ref="settings-editor" v-model="formValues.settings"
                :options="{ mode: 'code', enableSort: false, enableTransform: false }" :plus="false"
                style="min-height: 300px;" />
              <b-form-input id="settings-editor-error" v-model="settingsEditorError" v-validate="{ is: '' }"
                :state="validateState('settings-editor-error')" aria-describedby="settings-editor-error"
                data-vv-name="settings-editor-error" data-vv-as="Value" hidden autocomplete="off" />
              <b-form-invalid-feedback id="settings-editor-error" v-if="veeErrors.first('settings-editor-error')">
                {{ settingsEditorError }}
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </form>

      <b-button class="mt-2 float-right" variant="primary" @click="handleSubmit">Save</b-button>
    </b-modal>
  </span>
</template>

<script>

import Multiselect from 'vue-multiselect';
import HideControlsMapEditor from '../HideControlsMapEditor.vue';
import VJsoneditor from "v-jsoneditor/src/index";
import Vue from "vue";
import { getRouteIcon, ROUTE_CONFIGS } from "../../utils/routerIcons";
import draggable from 'vuedraggable';
import * as Roles from '../../definitions/Roles';

const tableTypes = [
  'Roulette',
  'AutomaticRoulette',
  'CroupierRoulette',
  'Cards',
  'Baccarat',
  'BlackJack',
  'Poker'
];
export default {
  data() {
    return {
      chosenHideControls: [],
      ratingTables: [],
      useDefaultSettingsForLobby: true,
      lobbyTables: [],
      lobbyTableGroups: [],
      lobbyTableGroupsAddValue: 0,
      lobbyTablesAddValue: 0,
      maxCoverageItems: [],
      localDefaultValues: JSON.parse(JSON.stringify(this.defaultValues)),

      urls: [
        { id: 'credit', label: 'Credit' },
        { id: 'debit', label: 'Debit' },
        { id: 'getBalance', label: 'Get balance' },
        { id: 'rollback', label: 'Rollback' },
      ],
      modalShow: false,
      formValues: this.getInitialValues(),
      settingsEditorError: '',
      urlsEditorError: '',
      isSpainServer: false,
      spainDefaultSettings: null,
      settingsChangedWarning: false,
    }
  },
  components: {
    HideControlsMapEditor,
    Multiselect,
    VJsoneditor,
    draggable
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  methods: {
    createFromTemplate() {
      const sourceId = this.formValues.id;
      this.modalShow = false;
      this.$emit('create-template', sourceId);
    },
    getInitialSettings() {
      return {
        redirectAfterRoundsWait: 10,
        bingoTicketLifetimeDays: 30,
        sendBetDetails: null,
        enableMinAdjust: null,
        enableMaxAdjust: null,
        disableNumberCompact: null,
        showTimeZone: null,
        disableOppositeBets: null,
        ssBrand: null,
        ssAuthToken: null,
        groupCredit: null,
        ratingTableIds: [],
        hideControls: [],
        hideControlsMap: {},
        showWorking: false,
        rollReverse: false,
        maxCoverage: null,
        maxCoverageMap: {},
        adjustComboBets: false,
      };
    },
    getInitialValues() {
      return {
        id: null,
        name: null,
        secret: null,
        settings: this.getInitialSettings(),
        urls: { 'credit': null, 'debit': null, 'getBalance': null, 'rollback': null },
        logo: null,
      };
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },
    resetModal() {
      this.setDefaults();
    },
    hideModal() {
      this.$refs['modal-container'].hide();
    },
    toggle() {
      this.modalShow = !this.modalShow;
    },
    handleSubmit() {
      try {
        this.$refs["settings-editor"].editor.get();
        this.settingsEditorError = '';
      } catch {
        this.settingsEditorError = 'The Settings must be a valid json string';
      }

      try {
        this.$refs["urls-editor"].editor.get();
        this.urlsEditorError = '';
      } catch {
        this.urlsEditorError = 'The Urls must be a valid json string';
      }

      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }

        const fd = new FormData();
        const basicKeys = ['name', 'secret'];

        for (let key of basicKeys) {
          if (null !== this.formValues[key]) {
            fd.append(key, this.formValues[key])
          }
        }

        const settingsToSave = { ...this.formValues.settings };
        if (settingsToSave.skin !== undefined) {
          delete settingsToSave.skin;
        }

        fd.append('settings', JSON.stringify(settingsToSave));
        fd.append('urls', JSON.stringify(this.formValues.urls));
        if (this.formValues.logo) {
          fd.append('logo', this.formValues.logo);
        }

        this
          .$http({
            url: '/api/v1/engine-operators' + (this.create ? '' : '/' + this.formValues.id),
            data: fd,
            method: 'POST',
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then(() => {
            setTimeout(() => {
              this.$emit('finish');
            }, 500);

            this.hideModal();
            this.$toastr.i('Success');
          })
          .catch((err) => {
            this.$toastr.e(JSON.stringify(err.response.data.message));

            throw err;
          });
      });
    },
    setDefaults() {
      this.maxCoverageItems = [];
      let initialValues = this.getInitialValues();

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field]);
      }

      if (this.localDefaultValues) {
        for (let field in this.localDefaultValues) {
          Vue.set(this.formValues, field, this.localDefaultValues[field]);
        }
      }

      if (this.formValues.settings && this.formValues.settings.skin !== undefined) {
        Vue.delete(this.formValues.settings, 'skin');
      }

      this.chosenHideControls = this.formValues.settings.hideControls?.map(control => ({
        value: control,
        text: control
      })) || [];

      const ratingTableIds = Array.isArray(this.formValues.settings.ratingTableIds)
        ? this.formValues.settings.ratingTableIds.map(Number).filter(Number.isFinite)
        : [];
      const tableOptions = this.$store.getters['tablesForSelect/list'] || [];
      this.ratingTables = ratingTableIds.map(tableId => {
        const value = tableId.toString();
        return tableOptions.find(table => table.value === value) || {
          value,
          label: 'Unknown table',
        };
      });

      this.useDefaultSettingsForLobby = true;

      if (this.formValues.settings.lobbyRows) {
        const hasOrderTables = Array.isArray(this.formValues.settings.lobbyRows.orderTables) &&
          this.formValues.settings.lobbyRows.orderTables.length > 0;
        const hasOrderGroups = Array.isArray(this.formValues.settings.lobbyRows.orderGroups) &&
          this.formValues.settings.lobbyRows.orderGroups.length > 0;

        if (hasOrderTables || hasOrderGroups) {
          this.useDefaultSettingsForLobby = false;

          if (hasOrderTables) {
            this.lobbyTables = this.formValues.settings.lobbyRows.orderTables.map(tableId => {
              const table = this.$store.getters['tablesForSelect/list']?.find(({ value }) => value === tableId.toString());
              return table ? { id: table.value, title: table.value + ' - ' + table.label } : null;
            }).filter(Boolean);
          }

          if (hasOrderGroups) {
            this.lobbyTableGroups = [...this.formValues.settings.lobbyRows.orderGroups];
          }
        }
      }

      const parsedItems = JSON.parse(JSON.stringify(this.localDefaultValues.settings.maxCoverageMap || {}));
      Object.keys(parsedItems).forEach(key => {
        this.maxCoverageItems.push({
          tableId: key,
          value: parsedItems[key]
        });
      });
    },
    async onShow() {
      this.localDefaultValues = JSON.parse(JSON.stringify(this.defaultValues));
      this.setDefaults();
      await this.loadSpainSettings();
      if (this.isSpainServer && this.create && this.spainDefaultSettings) {
        this.applySpainDefaults();
      }
      this.$nextTick(() => {
        this.checkSpainSettingsChanged();
      });
    },
    async loadSpainSettings() {
      try {
        const response = await this.$http.get('/api/v1/operators/default-settings');
        this.isSpainServer = response.data.hasDefaultSettings;
        this.spainDefaultSettings = response.data.defaultSettings;
      } catch (e) {
        this.isSpainServer = false;
        this.spainDefaultSettings = null;
      }
    },
    applySpainDefaults() {
      if (!this.spainDefaultSettings) return;

      const settings = this.formValues.settings || {};

      if (this.spainDefaultSettings.adjustLowBalance !== undefined) {
        Vue.set(settings, 'adjustLowBalance', this.spainDefaultSettings.adjustLowBalance);
      }
      if (this.spainDefaultSettings.enableMinAdjust !== undefined) {
        Vue.set(settings, 'enableMinAdjust', this.spainDefaultSettings.enableMinAdjust);
      }
      if (this.spainDefaultSettings.enableMaxAdjust !== undefined) {
        Vue.set(settings, 'enableMaxAdjust', this.spainDefaultSettings.enableMaxAdjust);
      }
      if (this.spainDefaultSettings.hideControls) {
        Vue.set(settings, 'hideControls', [...this.spainDefaultSettings.hideControls]);
        this.chosenHideControls = this.hideControlsOptions.filter(({ value }) =>
          this.spainDefaultSettings.hideControls.includes(value)
        );
      }
      if (settings.skin !== undefined) {
        Vue.delete(settings, 'skin');
      }

      this.formValues.settings = settings;
    },
    checkSpainSettingsChanged() {
      if (this.create) {
        this.settingsChangedWarning = false;
        return;
      }

      if (!this.isSpainServer || !this.spainDefaultSettings) {
        this.settingsChangedWarning = false;
        return;
      }

      const settings = this.formValues.settings || {};

      const normalizedSettings = { ...settings };
      if (normalizedSettings.enableMinAdjust !== undefined && normalizedSettings.minAjustEnable === undefined) {
        normalizedSettings.minAjustEnable = normalizedSettings.enableMinAdjust;
      }
      if (normalizedSettings.enableMaxAdjust !== undefined && normalizedSettings.maxAjustEnable === undefined) {
        normalizedSettings.maxAjustEnable = normalizedSettings.enableMaxAdjust;
      }

      for (const key in this.spainDefaultSettings) {
        const defaultValue = this.spainDefaultSettings[key];

        if (key === 'skin') {
          continue;
        }

        const settingValue = normalizedSettings[key];

        if (settingValue === undefined) {
          continue;
        }

        if (key === 'hideControls') {
          const hideControls = this.chosenHideControls
            .map(opt => (opt.value || '').toString().toLowerCase())
            .sort();
          const defaultHideControls = [...(defaultValue || [])]
            .map(value => (value || '').toString().toLowerCase())
            .sort();
          if (JSON.stringify(hideControls) !== JSON.stringify(defaultHideControls)) {
            this.settingsChangedWarning = true;
            return;
          }
          continue;
        }

        if (settingValue !== defaultValue) {
          this.settingsChangedWarning = true;
          return;
        }
      }

      this.settingsChangedWarning = false;
    },
    addLobbyTable(tableId) {
      if (tableId === 0) {
        return;
      }

      let table = this.$store.getters['tablesForSelect/list'].filter(({ value }) => value === tableId)[0];
      if (!table) {
        return;
      }
      this.lobbyTables.push({ id: table.value, title: table.value + ' - ' + table.label });
      this.lobbyTablesAddValue = 0;
    },
    addLobbyTableGroup(group) {
      if (group === 0) {
        return;
      }

      if (tableTypes.indexOf(group) === -1) {
        return;
      }

      this.lobbyTableGroups.push(group);
      this.lobbyTableGroupsAddValue = 0;
    },
    copyLobbySettings() {
      window.localStorage.setItem('buffer', JSON.stringify({
        lobbyTables: this.lobbyTables,
        lobbyTableGroups: this.lobbyTableGroups
      }));
      this.$toastr.i('Copied');
    },
    pasteLobbySettings() {
      try {
        let settings = JSON.parse(window.localStorage.getItem('buffer'));
        if (!Object.prototype.hasOwnProperty.call(settings, 'lobbyTables') || !Object.prototype.hasOwnProperty.call(settings, 'lobbyTableGroups')) {
          return;
        }

        this.lobbyTables = settings.lobbyTables;
        this.lobbyTableGroups = settings.lobbyTableGroups;
        this.$toastr.i('Pasted');
      } catch (e) {
        console.warn(e)
        return;
      }


    },
    addMaxCoverage() {
      this.maxCoverageItems.push({
        tableId: null,
        value: null,
      })
    },
    removeMaxCoverage(key) {
      this.$delete(this.maxCoverageItems, key)
    },
    removeLobbyTableGroup(group) {
      this.lobbyTableGroups = this.lobbyTableGroups.filter((item) => item !== group);
    },
    removeLobbyTable(tableId) {
      this.lobbyTables = this.lobbyTables.filter((table) => table.id !== tableId);
    },
    tableOptionLabel(option) {
      return option.value + ' - ' + option.label;
    },
  },
  computed: {
    isAdmin() {
      const user = this.$store.getters.currentUser;
      return user && user.type === Roles.ADMIN;
    },
    ratingTableOptions() {
      return this.$store.getters['tablesForSelect/list'] || [];
    },
    actualLobbyTables() {
      if (!this.useDefaultSettingsForLobby) {
        return this.lobbyTables || [];
      }

      let items = [];

      if (!this.localDefaultValues?.lobbyRowsDefaults) {
        return [];
      }

      let defaults = this.localDefaultValues.lobbyRowsDefaults.orderTables;
      if (!Array.isArray(defaults)) {
        return [];
      }

      defaults.forEach((tableId) => {
        let table = this.$store.getters['tablesForSelect/list']?.filter(({ value }) => value === tableId.toString())[0];
        if (!table) {
          return;
        }
        items.push({ id: table.value, title: table.value + ' - ' + table.label });
      });

      return items;
    },
    tableListForLobbyTables() {
      let options = [
        { value: 0, text: 'Add Table' }
      ];

      let usedIds = this.lobbyTables?.map((item) => item.id) || [];

      if (this.$store.getters['tablesForSelect/list']?.length) {
        this.$store.getters['tablesForSelect/list'].forEach(({ value, label }) => {
          if (usedIds.indexOf(value) !== -1) {
            return;
          }

          options.push({ value, text: value + ' - ' + label })
        });
      }

      return options;
    },
    actualLobbyTableGroups() {
      if (!this.useDefaultSettingsForLobby) {
        return this.lobbyTableGroups;
      }

      let items = [];

      if (!this.localDefaultValues.lobbyRowsDefaults) {
        return [];
      }

      let defaults = this.localDefaultValues.lobbyRowsDefaults.orderGroups;
      if (!Array.isArray(defaults)) {
        return [];
      }

      defaults.forEach((group) => {
        if (tableTypes.indexOf(group) === -1) {
          return;
        }

        items.push(group);
      });

      return items;
    },
    tableGroupList() {
      let options = [
        { value: 0, text: 'Add Table Type' }
      ];

      tableTypes.forEach((type) => {
        if (this.lobbyTableGroups.indexOf(type) !== -1) {
          return;
        }

        options.push({ value: type, text: type })
      });

      return options;
    },

    hideControlsOptions() {
      let options = [
        'Autobet',
        'Chat',
        'CompleteBet',
        'Favorites',
        'Fullscreen',
        'Exit',
        'Help',
        'History',
        'Language',
        'Lobby',
        'MiniLobby',
        'Stats',
        'Tournament',
        'Video',
        'Volume',
      ];

      return options.map(option => {
        return {
          value: option,
          text: option,
        };
      })
    },
    backUrl() {
      return process.env.VUE_APP_BACK_URL;
    },
    cdnBackUrl() {
      return process.env.VUE_APP_CDN_BACK_URL || "https://cdn-dev.alg4u.com/98";
    },
    routeIcon: () => getRouteIcon(ROUTE_CONFIGS),
  },
  watch: {
    useDefaultSettingsForLobby: {
      handler(newValue) {
        if (newValue) {
          Vue.delete(this.formValues.settings, 'lobbyRows');
          this.lobbyTables = [];
          this.lobbyTableGroups = [];
        }
      },
      deep: true
    },
    maxCoverageItems: {
      handler(newItems) {
        let maxCoverageMap = {};
        if (newItems.length > 0) {
          newItems.forEach((item) => {
            if (!item.tableId || !item.value) {
              return;
            }
            let tableId = parseInt(item.tableId);
            maxCoverageMap[tableId] = parseInt(item.value);
          });
        }
        this.formValues.settings.maxCoverageMap = maxCoverageMap;
      },
      deep: true
    },
    chosenHideControls: {
      handler(newValue) {
        this.formValues.settings.hideControls = newValue.map(item => item.value);
      },
      deep: true
    },
    ratingTables: {
      handler(newValue) {
        this.formValues.settings.ratingTableIds = newValue
          .map(item => Number(item.value))
          .filter(Number.isFinite);
      },
      deep: true
    },
    lobbyTables: {
      handler(newValue) {
        if (this.useDefaultSettingsForLobby) {
          return;
        }
        if (!this.formValues.settings.lobbyRows) {
          Vue.set(this.formValues.settings, 'lobbyRows', {});
        }
        Vue.set(
          this.formValues.settings.lobbyRows,
          'orderTables',
          newValue.map(table => parseInt(table.id))
        );
      },
      immediate: true,
      deep: true
    },
    lobbyTableGroups: {
      handler(newValue) {
        if (this.useDefaultSettingsForLobby) {
          return;
        }
        if (!this.formValues.settings.lobbyRows) {
          Vue.set(this.formValues.settings, 'lobbyRows', {});
        }
        Vue.set(
          this.formValues.settings.lobbyRows,
          'orderGroups',
          [...newValue]
        );
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Initial setup if needed
    // this.onShow();
  },
}
</script>
<style scoped>
.sortable-ghost {
  opacity: 0.5;
}

.draggable-disabled {
  background-color: #ececec;
}

.jsoneditor-container {
  height: 600px;
}

.tabs-container,
.json-editor-container {
  min-width: 0;
}

@media (max-width: 767px) {
  .modal-content-container {
    flex-direction: column;
  }
  .tabs-container,
  .json-editor-container {
    width: 100%;
    padding: 0 !important;
  }
}
</style>
